import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { AnyValue } from "@/lib/rowTypes";

export interface Account {
  id: string;
  user_id: string;
  account_type: string;
  account_name: string;
  subtitle: string | null;
  balance: number;
  status: string;
  unlock_condition: string | null;
  unlocked_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AccountTransaction {
  id: string;
  user_id: string;
  transaction_type: string;
  amount: number;
  description: string;
  source_account_id: string | null;
  destination_account_id: string | null;
  lesson_id: string | null;
  metadata: Record<string, AnyValue>;
  created_at: string;
}

const ACCOUNT_ORDER: string[] = [
  "checking", "savings", "emergency_fund", "roth_ira", "401k", "investment",
];

export function useAccounts() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);

  const fetchAccounts = useCallback(async () => {
    if (!user) { setAccounts([]); setLoading(false); return; }
    const { data } = await supabase
      .from("accounts")
      .select("*")
      .eq("user_id", user.id);
    const sorted = ((data) || []).sort(
      (a: Account, b: Account) =>
        ACCOUNT_ORDER.indexOf(a.account_type) - ACCOUNT_ORDER.indexOf(b.account_type)
    );
    setAccounts(sorted);
    setLoading(false);
  }, [user]);

  const fetchTransactions = useCallback(async () => {
    if (!user) { setTransactions([]); return; }
    const { data } = await supabase
      .from("account_transactions")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(30);
    setTransactions((data as AnyValue) || []);
  }, [user]);

  useEffect(() => {
    if (!user || initialized.current) return;
    initialized.current = true;
    const init = async () => {
      await supabase.rpc("ensure_user_accounts");
      await Promise.all([fetchAccounts(), fetchTransactions()]);
    };
    init();
  }, [user, fetchAccounts, fetchTransactions]);

  const transfer = async (fromType: string, toType: string, amount: number) => {
    const { error } = await supabase.rpc("transfer_between_accounts", {
      p_from_type: fromType,
      p_to_type: toType,
      p_amount: amount,
    });
    if (error) throw new Error(error.message);
    await Promise.all([fetchAccounts(), fetchTransactions()]);
  };

  const deposit = async (
    amount: number,
    description: string,
    type: string = "quiz_reward",
    lessonId?: string
  ) => {
    const { error } = await supabase.rpc("deposit_quiz_reward", {
      p_amount: amount,
      p_description: description,
      p_transaction_type: type,
      p_lesson_id: lessonId || null,
    });
    if (error) throw new Error(error.message);
    await Promise.all([fetchAccounts(), fetchTransactions()]);
  };

  const getAccount = (type: string) => accounts.find((a) => a.account_type === type);

  const netWorth = accounts.reduce((sum, a) => sum + a.balance, 0);
  const liquidCash = accounts
    .filter((a) => ["checking", "savings"].includes(a.account_type))
    .reduce((sum, a) => sum + a.balance, 0);
  const reserves = getAccount("emergency_fund")?.balance || 0;
  const retirement = accounts
    .filter((a) => ["roth_ira", "401k"].includes(a.account_type))
    .reduce((sum, a) => sum + a.balance, 0);
  const investments = getAccount("investment")?.balance || 0;

  const refetch = useCallback(async () => {
    await Promise.all([fetchAccounts(), fetchTransactions()]);
  }, [fetchAccounts, fetchTransactions]);

  return {
    accounts,
    transactions,
    loading,
    netWorth,
    liquidCash,
    reserves,
    retirement,
    investments,
    transfer,
    deposit,
    getAccount,
    refetch,
  };
}
